import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useAuth } from '../hooks/useAuth';
import { ChangePasswordRequest } from '../types/auth.types';

interface ChangePasswordFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const ChangePasswordForm: React.FC<ChangePasswordFormProps> = ({
                                                                        onSuccess,
                                                                        onCancel
                                                                      }) => {
  const { updatePassword, loading, error, clearAuthError } = useAuth();

  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [formErrors, setFormErrors] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [successMessage, setSuccessMessage] = useState('');

  const validateForm = () => {
    const errors = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    };
    let isValid = true;

    if (!formData.currentPassword) {
      errors.currentPassword = 'Поточний пароль обов\'язковий';
      isValid = false;
    }

    if (!formData.newPassword) {
      errors.newPassword = 'Новий пароль обов\'язковий';
      isValid = false;
    } else if (formData.newPassword.length < 6) {
      errors.newPassword = 'Пароль повинен містити мінімум 6 символів';
      isValid = false;
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Підтвердження пароля обов\'язкове';
      isValid = false;
    } else if (formData.newPassword !== formData.confirmPassword) {
      errors.confirmPassword = 'Паролі не збігаються';
      isValid = false;
    }

    if (formData.currentPassword === formData.newPassword) {
      errors.newPassword = 'Новий пароль повинен відрізнятися від поточного';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    clearAuthError();
    setSuccessMessage('');

    const changePasswordData: ChangePasswordRequest = {
      currentPassword: formData.currentPassword,
      newPassword: formData.newPassword,
    };

    try {
      await updatePassword(changePasswordData);
      setSuccessMessage('Пароль успішно змінено. Всі сесії скинуто.');
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });

      if (onSuccess) {
        setTimeout(() => onSuccess(), 2000);
      }
    } catch (error) {
      console.error('Помилка зміни пароля:', error);
    }
  };

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value,
    }));

    // Очищаємо помилку для поля при зміні
    if (formErrors[field as keyof typeof formErrors]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }

    // Очищаємо повідомлення при зміні
    if (successMessage) setSuccessMessage('');
    if (error) clearAuthError();
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 4,
        maxWidth: 400,
        mx: 'auto',
        mt: 4,
      }}
    >
      <Typography variant="h5" component="h2" gutterBottom align="center">
        Змінити пароль
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <TextField
          margin="normal"
          required
          fullWidth
          label="Поточний пароль"
          type="password"
          value={formData.currentPassword}
          onChange={handleChange('currentPassword')}
          autoComplete="current-password"
          error={!!formErrors.currentPassword}
          helperText={formErrors.currentPassword}
        />

        <TextField
          margin="normal"
          required
          fullWidth
          label="Новий пароль"
          type="password"
          value={formData.newPassword}
          onChange={handleChange('newPassword')}
          autoComplete="new-password"
          error={!!formErrors.newPassword}
          helperText={formErrors.newPassword}
        />

        <TextField
          margin="normal"
          required
          fullWidth
          label="Підтвердіть новий пароль"
          type="password"
          value={formData.confirmPassword}
          onChange={handleChange('confirmPassword')}
          autoComplete="new-password"
          error={!!formErrors.confirmPassword}
          helperText={formErrors.confirmPassword}
        />

        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Змінити пароль'}
          </Button>

          {onCancel && (
            <Button
              variant="outlined"
              fullWidth
              onClick={onCancel}
              disabled={loading}
            >
              Скасувати
            </Button>
          )}
        </Box>
      </Box>
    </Paper>
  );
};